import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, TrendingUp, Code2, Brain, Blocks } from 'lucide-react';
import { Button } from '../components/ui/button';
import IdeaCard from '../components/IdeaCard';
import SEO, { websiteStructuredData, organizationStructuredData } from '../components/SEO';
import api, { trackPageView } from '../lib/api';

const HomePage = () => {
  const [featuredIdeas, setFeaturedIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('/');
    fetchFeaturedIdeas();
  }, []);

  const fetchFeaturedIdeas = async () => {
    try {
      const response = await api.get('/ideas?featured=true');
      setFeaturedIdeas(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const combinedStructuredData = [websiteStructuredData, organizationStructuredData];

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Solutions',
      description: 'Cutting-edge artificial intelligence and machine learning innovations ready for deployment.',
    },
    {
      icon: Blocks,
      title: 'Blockchain Technology',
      description: 'Decentralized systems and smart contract solutions for the next generation of applications.',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Battle-tested security architectures designed for mission-critical systems.',
    },
    {
      icon: Code2,
      title: 'Full-Stack Systems',
      description: 'Complete software solutions with scalable backend and modern frontend architectures.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        url="/"
        structuredData={combinedStructuredData}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-pattern overflow-hidden pt-16">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1645839078449-124db8a049fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjBuZXR3b3JrJTIwZ2xvd2luZyUyMG5vZGVzJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMTYyMzIxfDA&ixlib=rb-4.1.0&q=85')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/60 to-slate-950" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8">
              <Zap className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-400">Where Ideas Meet Capital</span>
            </div>

            {/* Heading */}
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              <span className="text-white">Transform</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-violet-400 to-pink-400 bg-clip-text text-transparent">Innovative Ideas</span>
              <br />
              <span className="text-white">Into Reality</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-slate-300 max-w-xl mb-10 leading-relaxed">
              Discover groundbreaking software and systems concepts. 
              Acquire proven ideas or invest in the development of tomorrow's technology.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/ideas" data-testid="hero-browse-ideas-btn">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-6 text-lg rounded-md shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all hover:scale-105 w-full sm:w-auto">
                  Browse Ideas
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="bg-white/5 hover:bg-white/10 text-white border border-white/10 px-8 py-6 text-lg rounded-md backdrop-blur-md"
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-950" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
              Innovation Categories
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Explore ideas across multiple technology domains, each vetted for market potential and technical feasibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="group p-6 rounded-xl bg-slate-900/50 border border-white/10 hover:border-cyan-500/30 transition-all duration-300 hover:-translate-y-1"
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                    <Icon className="h-6 w-6 text-cyan-400" />
                  </div>
                  <h3 className="font-heading font-semibold text-white text-lg mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Ideas Section */}
      <section className="py-24 bg-slate-900/30" data-testid="featured-ideas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-2">
                Featured Ideas
              </h2>
              <p className="text-slate-400">
                Handpicked innovations ready for acquisition or investment.
              </p>
            </div>
            <Link to="/ideas" data-testid="view-all-ideas-link">
              <Button variant="ghost" className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                View All Ideas
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, idx) => (
                <div key={idx} className="h-64 rounded-xl skeleton" />
              ))}
            </div>
          ) : featuredIdeas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredIdeas.map((idea) => (
                <IdeaCard key={idea.id} idea={idea} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">No featured ideas yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-12 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-white/10">
            <TrendingUp className="h-12 w-12 text-cyan-400 mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Ready to Invest in Innovation?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Join forward-thinking entrepreneurs and investors who are shaping the future of technology.
            </p>
            <Link to="/ideas" data-testid="cta-explore-btn">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-4 text-lg rounded-md shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all hover:scale-105">
                Explore Opportunities
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
